import Fonts from "@/services/canvas/fonts/Fonts";

const hostURL = import.meta.env.VITE_ROOT_HOST;
import {TEMPLATE_TYPES} from "@/models/TEMPLATE_TYPES";
import { NodeTemplate, createNodeTemplateObject } from '@/modules/Node';
import createImage from "@/services/canvas/utils/CreateImage";
import { EdgeTemplate, createEdgeTemplateObject } from '@/modules/Edge';
import { fillTemplateGapsWithNull } from './templateGaps';
const {fontFamilyGetNameByUrl} = Fonts();

export default {
  namespaced: true,
  state: {
    objectTemplateURL: '/templates/objects/',
    objectTemplates: [],
    objectTemplateEdited: null,
    objectTemplateChosen: null,
  },
  getters: {
    getClientFonts(state, getters, rootState, rootGetters){
      return rootGetters["fonts/getClientFonts"]
    },
    getTemplates(state, getters, rootState, rootGetters){
      return rootGetters["design/getTemplates"]
    },
    getObjectTemplates(state){
      return state.objectTemplates;
    },
    getObjectTemplateEdited(state){
      return state.objectTemplateEdited;
    },
    getObjectTemplateChosen(state){
      return state.objectTemplateChosen;
    },
    getObjectTemplateURL(state){
      return state.objectTemplateURL;
    },
  },
  mutations: {
    SET_OBJECT_TEMPLATE_EDITED(state, id){
      state.objectTemplateEdited = id;
    },
    SET_OBJECT_TEMPLATE_CHOSEN(state, templateChosen){
      state.objectTemplateChosen = templateChosen;
    },
    SET_OBJECT_TEMPLATE_OCCUPIED(state, {id, value}){
      const template = state.objectTemplates.find(template => template.id === id);
      if(template) template.occupied = value;
    },
    DELETE_OBJECT_TEMPLATE(state, id){
      const templateIndex = state.objectTemplates.findIndex(template => template.id === id);
      state.objectTemplates.splice(templateIndex, 1);
    },
    CLEAR_OBJECT_TEMPLATES(state){
      state.objectTemplates = [];
    }
  },
  actions: {
    async objectTemplatesInit({state, getters, commit, rootGetters}){
      const fonts = getters.getClientFonts;
      const templates = getters.getTemplates;
      // KAIA-seeded templates (the nav template, blank-map depth templates) carry
      // only an id/name/type plus a few overrides. The panel builds a renderable
      // template from EVERY design field, so a partial template fails to build and
      // never appears. Add the missing keys as NULL (not the defaults' values):
      // null means "inherit", so template inheritance (defaults → global design →
      // template) still resolves them; a real value would shadow the global layer.
      // Complete (e.g. base-map) templates are unaffected: their own fields win.
      const elementsDesign = rootGetters['design/getElementsDesign'] ?? {};
      const nodeDefaults = elementsDesign.defaults?.nodes ?? {};
      const edgeDefaults = elementsDesign.defaults?.edges ?? {};

      for(const template of Object.values(templates)){
        // Isolate a malformed template so one bad entry can't abort the whole loop
        // and leave the panel empty (see "Unknown node shape" regression).
        try {
          if(template.type === TEMPLATE_TYPES.NODE) {
            const nodeTemplate = new NodeTemplate(createNodeTemplateObject(fillTemplateGapsWithNull(template, nodeDefaults), fonts));
            await nodeTemplate.init();
            const media = nodeTemplate.media;
            const mediaURL = nodeTemplate.mediaURL;
            if(media){
              nodeTemplate.image = createImage(media.includes(hostURL) ? media :hostURL + media, nodeTemplate.init.bind(nodeTemplate), true);
            }
            if(mediaURL){
              nodeTemplate.image = createImage(mediaURL, nodeTemplate.init.bind(nodeTemplate), true)
            }
            state.objectTemplates.push(nodeTemplate)
          }
          if(template.type === TEMPLATE_TYPES.EDGE){
            const edgeTemplate = new EdgeTemplate(createEdgeTemplateObject(fillTemplateGapsWithNull(template, edgeDefaults), fonts));
            edgeTemplate.cacheInstanceSetDimensions();
            edgeTemplate.cache();
            edgeTemplate.makeSnapshot();
            state.objectTemplates.push(edgeTemplate);
          }
        } catch(err) {
          console.error(`Failed to build object template ${template?.id}`, err);
        }
      }
    },
    async objectTemplateUpdateByData({getters, commit}, templateData){
      const {id, data} = templateData
      const objectTemplates = getters.getObjectTemplates;
      const objectTemplate = objectTemplates.find(template => template.id === parseInt(id));
      const fonts = getters.getClientFonts;

      if(data.type === TEMPLATE_TYPES.NODE){
        const dataConverted = createNodeTemplateObject(data, fonts);
        delete dataConverted.image;

        if(dataConverted.media !== objectTemplate.media){
          objectTemplate.media = dataConverted.media;
          if(objectTemplate.media) objectTemplate.image = createImage(objectTemplate.media, objectTemplate.init.bind(objectTemplate), true);
        }
        if(dataConverted.mediaURL !== objectTemplate.mediaURL){
          objectTemplate.mediaURL = dataConverted.mediaURL;
          if(objectTemplate.mediaURL) objectTemplate.image = createImage(objectTemplate.mediaURL, objectTemplate.init.bind(objectTemplate), true);
        }
        if(!dataConverted.media && !dataConverted.mediaURL) objectTemplate.image = null;

        Object.assign(objectTemplate, dataConverted);
        await objectTemplate.init();
      }

      if(data.type === TEMPLATE_TYPES.EDGE){
        const dataConverted = createEdgeTemplateObject(data, fonts);
        Object.assign(objectTemplate, dataConverted);
        objectTemplate.cacheInstanceSetDimensions();
        objectTemplate.cache();
        objectTemplate.makeSnapshot();
      }
    },
    async objectTemplateUpdate({getters, commit, dispatch}, {id, data}){
      const objectTemplates = getters.getObjectTemplates;
      if(Object.keys(objectTemplates).length === 0){
        await dispatch('objectTemplatesInit')
      }
      const objectTemplate = objectTemplates.find(template => template.id === id);
      const fonts = getters.getClientFonts;

      for(let [key, value] of Object.entries(data)){
        if(key === 'fontFamily') {
          objectTemplate.fontFamily = fontFamilyGetNameByUrl(value, fonts);
          break;
        }
        if((key === 'media' && !value) && !objectTemplate.mediaURL) {
          objectTemplate.image = null;
          objectTemplate.media = null;
          objectTemplate.mediaURL = null;
          break;
        }
        if(key === 'media' && value){
          objectTemplate.media = value;
          objectTemplate.mediaURL = null;
          // class instance's method should be bind to the instance to have this
          objectTemplate.image = createImage(value, objectTemplate.init.bind(objectTemplate), true);
          break;
        }
        if(key === 'mediaURL' && value){
          objectTemplate.image = createImage(value, objectTemplate.init.bind(objectTemplate), true);
          objectTemplate.media = null;
          objectTemplate.mediaURL = value;
          break;
        }
        objectTemplate[key] = value;
      }

      objectTemplate.cacheInstanceSetDimensions();
      objectTemplate.cacheClear();
      await objectTemplate.cache();
      objectTemplate.makeSnapshot();
    },
    async objectTemplateCreate({state, getters, commit}, {id}){
      const fonts = getters.getClientFonts;
      const template = getters.getTemplates[id];

      // Guard a malformed template so a failed build can't corrupt objectTemplates state.
      try {
        if(template.type === TEMPLATE_TYPES.NODE) {
          const nodeTemplate = new NodeTemplate(createNodeTemplateObject(template, fonts));
          await nodeTemplate.init();
          const media = nodeTemplate.media;
          const mediaURL = nodeTemplate.mediaURL;
          if(media){
            nodeTemplate.image = createImage(media.includes(hostURL) ? media :hostURL + media, nodeTemplate.init.bind(nodeTemplate), true);
          }
          if(mediaURL){
            nodeTemplate.image = createImage(mediaURL, nodeTemplate.init.bind(nodeTemplate), true)
          }
          state.objectTemplates.push(nodeTemplate)
        }
        if(template.type === TEMPLATE_TYPES.EDGE){
          const edgeTemplate = new EdgeTemplate(createEdgeTemplateObject(template, fonts));
          edgeTemplate.cacheInstanceSetDimensions();
          edgeTemplate.cache();
          edgeTemplate.makeSnapshot();
          state.objectTemplates.push(edgeTemplate);
        }
      } catch(err) {
        console.error(`Failed to create object template ${id}`, err);
      }
    },
    async templateUpdateOnFontLoaded({getters, commit}, fontData){
      const {fontFamily} = fontData;
      const templates = getters.getObjectTemplates;
      for(const template of templates){
        if(template.fontFamily === fontFamily){
          template.cacheInstanceSetDimensions();
          template.cacheClear();
          await template.cache();
          template.makeSnapshot();
        }
      }
    },
    objectTemplateDelete({commit}, templateId){
      commit('DELETE_OBJECT_TEMPLATE', templateId);
    },
    objectTemplateSetEdited({commit}, id){
      commit('SET_OBJECT_TEMPLATE_EDITED', id);
    },
    objectTemplateSetChosen({commit}, chosenTemplate){
      commit('SET_OBJECT_TEMPLATE_CHOSEN', chosenTemplate);
    },
    objectTemplateSetOccupied({commit}, {id, value}){
      commit('SET_OBJECT_TEMPLATE_OCCUPIED', {id, value});
    },
    objectTemplatesClear({commit}){
      commit('CLEAR_OBJECT_TEMPLATES');
    },
  }
}